import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { AppDialogWrapperService } from '../../../ni-components/app-dialog-wrapper/app-dialog-wrapper.service';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { forEach, map, sortBy } from 'lodash';
import * as XLSX from 'xlsx-js-style';

// Components
import {
    ColumnType,
} from '../../../shared/components/table-settings/table-settings.component';
import { Subscription } from 'rxjs';
import { CustomersGridViewModel, FboPriceInfo } from '../../../models/customers/CustomerGridViewModel';
import { GroupCustomerPricingGridColumnId } from '../../../models/customers/GroupCustomerPricingGridColumnId';
import { Router } from '@angular/router';
import { SharedService } from '../../../layouts/shared-service';
import { CustomersEditDialogComponent } from '../../customers/customers-edit-dialog/customers-edit-dialog.component';
import { GridBase } from '../../../services/tables/GridBase';
import { CurrencyPresicionPipe } from '../../../shared/pipes/decimal/currencyPresicion.pipe';

export interface CustomerRow {
    company: string;
    customerInfoByGroupId: number;

    fboPriceInfo: FboPriceInfo[];

    [key: string]: any;
}


@Component({
    selector: 'app-group-customers-pricing-grid',
    styleUrls: ['./group-customers-pricing-grid.component.scss'],
    templateUrl: './group-customers-pricing-grid.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class GroupCustomersPricingGridComponent extends GridBase implements OnInit, AfterViewInit {
    // Input/Output Bindings
    @Input() customersData: CustomersGridViewModel[];

    // Members
    @ViewChild('customerTableContainer') table: ElementRef;
    @ViewChild('scrollContainer') scrollContainer: ElementRef;
    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;

    customersDataSource: MatTableDataSource<CustomerRow> = null;
    customerFilterType: number = undefined;
    pageIndex = 0;
    pageSize = 100;
    displayedColumns: ColumnType[] = [];
    columns: GroupCustomerPricingGridColumnId[] = [];
    columnIds: string[] = [];
    dynamicColumnIds: string[] = [];
    groupHeaderColumns: { id: string; name: string }[] = [];
    groupHeaderIds: string[] = [];
    columnLabels: Map<string, string> = new Map();
    columnGroupClasses: Map<string, string> = new Map();
    start: number = 0;
    limit: number = 20;
    end: number = this.limit + this.start;
    private icaos: string[] = [];
    private fboInfoMap = new Map<string, FboPriceInfo>();

    results = '[]';
    sortChangeSubscription: Subscription;
    hoveredIcao: string | null = null;
    hoveredRowId: number | null = null;
    private scrollListener: (() => void) | null = null;
    constructor(
        private router: Router,
        private sharedService: SharedService,
        private currencyPresicion: CurrencyPresicionPipe,
        private appDialogWrapperService: AppDialogWrapperService,
        private cdr: ChangeDetectorRef,
    ) {
        super();
    }

    ngOnInit() {
        this.refreshCustomerDataSource();
    }
    ngAfterViewInit() {
        setTimeout(() => this.setupStickyHeaders());
    }
    ngOnDestroy() {
        this.sortChangeSubscription?.unsubscribe();
        if (this.scrollListener) {
            this.scrollContainer?.nativeElement?.removeEventListener('scroll', this.scrollListener);
        }
    }
    onPageChanged(event: any) {
        localStorage.setItem('pageIndex', event.pageIndex);
        sessionStorage.setItem(
            'pageSizeValue',
            this.paginator.pageSize.toString()
        );
        forEach(this.customersData, (customer) => {
            customer.selectAll = false;
        });
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.customersData) {
            this.setVirtualScrollVariables(this.paginator, this.sort, this.customersData);
            this.refreshCustomerDataSource();
        }
    }

    exportCustomersToExcel() {
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        const ws: XLSX.WorkSheet = {};

        const headerStyle = {
            font: { bold: true, sz: 11 },
            fill: { fgColor: { rgb: "F8F9FA" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: {
                top: { style: "thin", color: { rgb: "DEE2E6" } },
                bottom: { style: "medium", color: { rgb: "DEE2E6" } },
                left: { style: "thin", color: { rgb: "E9ECEF" } },
                right: { style: "thin", color: { rgb: "E9ECEF" } }
            }
        };

        const subHeaderStyle = {
            font: { bold: true, sz: 10 },
            fill: { fgColor: { rgb: "F8F9FA" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: {
                top: { style: "thin", color: { rgb: "DEE2E6" } },
                bottom: { style: "medium", color: { rgb: "DEE2E6" } },
                left: { style: "thin", color: { rgb: "E9ECEF" } },
                right: { style: "thin", color: { rgb: "E9ECEF" } }
            }
        };

        const cellStyle = {
            font: { sz: 10 },
            alignment: { horizontal: "left", vertical: "center" },
            border: {
                top: { style: "thin", color: { rgb: "E9ECEF" } },
                bottom: { style: "thin", color: { rgb: "E9ECEF" } },
                left: { style: "thin", color: { rgb: "E9ECEF" } },
                right: { style: "thin", color: { rgb: "E9ECEF" } }
            }
        };

        const thickBorderRight = {
            border: {
                top: { style: "thin", color: { rgb: "E9ECEF" } },
                bottom: { style: "thin", color: { rgb: "E9ECEF" } },
                left: { style: "thin", color: { rgb: "E9ECEF" } },
                right: { style: "medium", color: { rgb: "495057" } }
            }
        };

        let colIndex = 0;
        const merges: XLSX.Range[] = [];
        const colWidths = [{ wch: 30 }];

        ws['A1'] = { v: '', t: 's', s: headerStyle };
        colIndex = 1;

        this.icaos.forEach((icao, idx) => {
            const startCol = XLSX.utils.encode_col(colIndex);

            // Put ICAO in the first cell of the merge
            ws[`${startCol}1`] = { v: icao, t: 's', s: headerStyle };

            // Merge the 3 cells
            merges.push({
                s: { r: 0, c: colIndex },
                e: { r: 0, c: colIndex + 2 }
            });

            // Set column widths
            colWidths.push({ wch: 20 });
            colWidths.push({ wch: 18 });
            colWidths.push({ wch: 12 });

            colIndex += 3;
        });

        ws['A2'] = { v: 'Company', t: 's', s: { ...subHeaderStyle, ...thickBorderRight } };
        colIndex = 1;

        this.icaos.forEach((icao, idx) => {
            const col1 = XLSX.utils.encode_col(colIndex);
            const col2 = XLSX.utils.encode_col(colIndex + 1);
            const col3 = XLSX.utils.encode_col(colIndex + 2);

            ws[`${col1}2`] = { v: 'ITP Pricing Template', t: 's', s: subHeaderStyle };
            ws[`${col2}2`] = { v: 'Pricing Formula', t: 's', s: subHeaderStyle };
            ws[`${col3}2`] = {
                v: 'All In Price',
                t: 's',
                s: { ...subHeaderStyle, ...thickBorderRight }
            };

            colIndex += 3;
        });

        let rowIndex = 3;
        this.customersDataSource.filteredData.forEach(row => {
            colIndex = 0;

            ws[`A${rowIndex}`] = {
                v: row.company,
                t: 's',
                s: {
                    ...cellStyle,
                    ...thickBorderRight,
                    alignment: { horizontal: "left", vertical: "center" }
                }
            };
            colIndex = 1;

            this.icaos.forEach((icao, icaoIdx) => {
                const col1 = XLSX.utils.encode_col(colIndex);
                const col2 = XLSX.utils.encode_col(colIndex + 1);
                const col3 = XLSX.utils.encode_col(colIndex + 2);

                const template = row[`${icao}_pricingTemplateName`] || '';
                const formula = row[`${icao}_pricingFormula`] || '';
                const priceValue = row[`${icao}_allInPrice`];

                let price: any = '';
                let cellFormat: any = { ...cellStyle, ...thickBorderRight };

                if (priceValue && priceValue !== 'N/A') {
                    const cleanValue = priceValue.toString().replace(/[$,]/g, '');

                    const decimalMatch = cleanValue.match(/\.(\d+)/);
                    const decimalPlaces = decimalMatch ? decimalMatch[1].length : 0;

                    const numericPrice = parseFloat(cleanValue);

                    if (!isNaN(numericPrice)) {
                        price = numericPrice;

                        if (decimalPlaces > 0) {
                            cellFormat.numFmt = '0.' + '0'.repeat(decimalPlaces);
                        } else {
                            cellFormat.numFmt = '0';
                        }
                    }
                }


                ws[`${col1}${rowIndex}`] = { v: template, t: 's', s: cellStyle };
                ws[`${col2}${rowIndex}`] = { v: formula, t: 's', s: cellStyle };
                ws[`${col3}${rowIndex}`] = {
                    v: price !== '' ? price : '',
                    t: price !== '' ? 'n' : 's',
                    z: price !== '' ? '0.0000' : undefined, 
                    s: cellFormat
                } as any;

                colIndex += 3;
            });

            rowIndex++;
        });

        const totalCols = 1 + (this.icaos.length * 3);
        const totalRows = rowIndex - 1;
        ws['!ref'] = `A1:${XLSX.utils.encode_col(totalCols - 1)}${totalRows}`;
        ws['!merges'] = merges;
        ws['!cols'] = colWidths;

        XLSX.utils.book_append_sheet(wb, ws, 'Group View - Customer Pricing');
        XLSX.writeFile(wb, 'Group_View_Customer_Pricing.xlsx');
    }

    getSecondHeaderLabel(colId: string): string {
        const id = colId as GroupCustomerPricingGridColumnId;

        if (id === 'company')
            return 'Company';

        const parts = id.split('_');
        const suffix = parts[parts.length - 1];

        switch (suffix) {
            case 'pricingTemplateName':
                return 'ITP Pricing Template';
            case 'pricingFormula':
                return 'Pricing Formula';
            case 'allInPrice':
                return 'All‑In Price';
            default:
                return '';
        }
    }

    private computeFboGroupClass(colId: string): string {
        if (colId === 'company') {
            return '';
        }

        const parts = colId.split('_');
        parts.pop();
        const icao = parts[0];
        const icaoIndex = this.icaos.indexOf(icao);
        return icaoIndex % 2 === 0 ? 'fbo-group-light' : 'fbo-group-dark';
    }

    onCellClick(row: CustomerRow, colId: string): void {
        if (colId === 'company') {
            return;
        }

        const parts = colId.split('_');
        parts.pop();
        const icao = parts[0];

        const fboInfo = row.fboPriceInfo.find(fbo => fbo.icao === icao);

        if (fboInfo && fboInfo.fboId) {
            this.sharedService.currentUser.fboId = fboInfo.fboId;
            this.sharedService.currentUser.decimalPrecision = fboInfo.decimalPrecision;

            const dialogWrapper = this.appDialogWrapperService.open<any>({
                title: fboInfo.icao || 'Edit Customer',
                component: CustomersEditDialogComponent,
                data: {
                    customerInfoByGroupId: row.customerInfoByGroupId,
                    showCustomerDetailsTab: false,
                    showPricingTab: true,
                    title: fboInfo.icao
                },
                hideHeader: true
            });

            dialogWrapper.afterClosed().subscribe((result: any) => {
                this.sharedService.currentUser.fboId = 0;
                this.sharedService.currentUser.decimalPrecision = 4;
                if (result) {
                    this.updateRowData(row, fboInfo.icao, result);
                }
            });

            dialogWrapper.afterClosed().subscribe(result => {
                this.sharedService.currentUser.fboId = 0;
                this.sharedService.currentUser.decimalPrecision = 4;
            });
        }
    }

    isClickableColumn(colId: string): boolean {
        return colId !== 'company';
    }

    trackByCustomerId(index: number, row: CustomerRow): number {
        return row.customerInfoByGroupId;
    }

    onCellMouseEnter(row: CustomerRow, colId: string): void {
        if (colId === 'company') {
            this.hoveredIcao = null;
            this.hoveredRowId = null;
            return;
        }

        const parts = colId.split('_');
        parts.pop();
        this.hoveredIcao = parts.join('_');
        this.hoveredRowId = row.customerInfoByGroupId;
        this.cdr.markForCheck();
    }

    onCellMouseLeave(): void {
        this.hoveredIcao = null;
        this.hoveredRowId = null;
        this.cdr.markForCheck();
    }

    isCellInHoveredGroup(row: CustomerRow, colId: string): boolean {
        if (!this.hoveredIcao || !this.hoveredRowId || colId === 'company') {
            return false;
        }

        return row.customerInfoByGroupId === this.hoveredRowId &&
            colId.startsWith(this.hoveredIcao + '_');
    }

    private refreshCustomerDataSource(): void {
        this.sortChangeSubscription = this.sort.sortChange.subscribe(() => {
            this.displayedColumns = this.displayedColumns.map(col =>
                col.id === this.sort.active
                    ? { ...col, sort: this.sort.direction }
                    : { hidden: col.hidden, id: col.id, name: col.name }
            );
        });

        if (!this.customersDataSource) {
            this.customersDataSource = new MatTableDataSource<CustomerRow>();
        }

        this.fboInfoMap.clear();
        this.icaos = [];

        this.customersData.forEach(row => {
            row.fboPriceInfo.forEach(fbo => {
                if (!this.fboInfoMap.has(fbo.icao)) {
                    this.fboInfoMap.set(fbo.icao, fbo);
                    this.icaos.push(fbo.icao); // raw name for the top header
                }
            });
        });

        this.groupHeaderColumns = [
            { id: 'company_group', name: 'Company' } 
        ];

        this.icaos.forEach(rawName => {
            this.groupHeaderColumns.push({
                id: rawName + '_group',
                name: rawName
            });
        });

        this.groupHeaderIds = this.groupHeaderColumns.map(g => g.id);

        const staticIds: string[] = ['company']; 
        const dynamicIds: string[] = [];

        this.icaos.forEach(rawName => {
            const base = rawName;
            dynamicIds.push(`${base}_pricingTemplateName`);
            dynamicIds.push(`${base}_pricingFormula`);
            dynamicIds.push(`${base}_allInPrice`);
        });

        this.columnIds = [...staticIds, ...dynamicIds];
        this.dynamicColumnIds = dynamicIds;
        this.displayedColumns = this.columnIds.map(id => {
            const label = this.getSecondHeaderLabel(id as GroupCustomerPricingGridColumnId);
            this.columnLabels.set(id, label);
            this.columnGroupClasses.set(id, this.computeFboGroupClass(id));
            return {
                id,
                name: label,
                hidden: false,
            };
        });

        const flattenedData: CustomerRow[] = this.customersData.map(row => {
            const flatRow: CustomerRow = {
                company: row.company,
                customerInfoByGroupId: row.customerInfoByGroupId,
                fboPriceInfo: row.fboPriceInfo,
            };

            // Pre-compute all dynamic column values
            this.icaos.forEach(icao => {
                const fboInfo = row.fboPriceInfo.find(fbo => fbo.icao === icao);

                if (fboInfo) {
                    flatRow[`${icao}_pricingTemplateName`] = fboInfo.pricingTemplateName == "" ? "*Missing Template*" : fboInfo.pricingTemplateName;
                    flatRow[`${icao}_pricingFormula`] = fboInfo.pricingFormula == "NULL" ? "N/A" : fboInfo.pricingFormula;
                    flatRow[`${icao}_allInPrice`] = !isNaN(fboInfo.allInPrice) && fboInfo.allInPrice !== 0
                        ? this.currencyPresicion.transform(fboInfo.allInPrice)
                        : fboInfo.isPricingExpired ? 'Expired' : 'N/A';
                } else {
                    flatRow[`${icao}_pricingTemplateName`] = '';
                    flatRow[`${icao}_pricingFormula`] = '';
                    flatRow[`${icao}_allInPrice`] = '';
                }
            });

            return flatRow;
        });

        this.customersDataSource.data = flattenedData;
        this.sort.active = 'company';
        this.customersDataSource.sort = this.sort;
        this.customersDataSource.paginator = this.paginator;
        this.cdr.markForCheck();

        // Re-setup sticky headers after data source changes
        setTimeout(() => this.setupStickyHeaders());
    }

    private setupStickyHeaders(): void {
        const container = this.scrollContainer?.nativeElement;
        if (!container) return;

        // Remove old listener
        if (this.scrollListener) {
            container.removeEventListener('scroll', this.scrollListener);
        }

        const headerCells = container.querySelectorAll('.mat-header-cell') as NodeListOf<HTMLElement>;
        if (!headerCells.length) return;

        this.scrollListener = () => {
            const scrollTop = container.scrollTop;
            const scrollLeft = container.scrollLeft;

            // Move all header cells down to follow vertical scroll
            headerCells.forEach((cell: HTMLElement) => {
                cell.style.transform = `translateY(${scrollTop}px)`;
                cell.style.position = 'relative';
                cell.style.zIndex = '100';
                cell.style.backgroundColor = '#f8f9fa';
            });

            // Company header cells: also follow horizontal scroll
            const companyHeaderCells = container.querySelectorAll(
                '.mat-header-cell.mat-column-company, .mat-header-cell.mat-column-company_group'
            ) as NodeListOf<HTMLElement>;
            companyHeaderCells.forEach((cell: HTMLElement) => {
                cell.style.transform = `translate(${scrollLeft}px, ${scrollTop}px)`;
                cell.style.zIndex = '104';
            });

            // Company data cells: follow horizontal scroll
            const companyCells = container.querySelectorAll('.mat-cell.mat-column-company') as NodeListOf<HTMLElement>;
            companyCells.forEach((cell: HTMLElement) => {
                cell.style.transform = `translateX(${scrollLeft}px)`;
                cell.style.position = 'relative';
                cell.style.zIndex = '99';
                cell.style.backgroundColor = '#ffffff';
            });
        };

        container.addEventListener('scroll', this.scrollListener);
    }

    private updateRowData(row: CustomerRow, icao: string, updatedData: any): void {
        const displayIndex = this.customersDataSource.data.findIndex(
            r => r.customerInfoByGroupId === row.customerInfoByGroupId
        );

        if (displayIndex !== -1) {
            const updatedRows = [...this.customersDataSource.data];

            updatedRows[displayIndex][`${icao}_pricingTemplateName`] =
                updatedData.pricingTemplateName ?? '';
            updatedRows[displayIndex][`${icao}_pricingFormula`] =
                updatedData.pricingFormula !== 'NULL' ? updatedData.pricingFormula : '';

            const price = Number(updatedData.allInPrice);
            updatedRows[displayIndex][`${icao}_allInPrice`] = !isNaN(price) && price !== 0
                ? this.currencyPresicion.transform(price)
                : updatedData.isPricingExpired == true ? 'Expired' : 'N/A';

            const fboInfoIndex = updatedRows[displayIndex].fboPriceInfo.findIndex(
                fbo => fbo.icao === icao
            );

            if (fboInfoIndex !== -1) {
                updatedRows[displayIndex].fboPriceInfo[fboInfoIndex] = {
                    ...updatedRows[displayIndex].fboPriceInfo[fboInfoIndex],
                    pricingTemplateName: updatedData.pricingTemplateName,
                    pricingFormula: updatedData.pricingFormula,
                    allInPrice: updatedData.allInPrice
                };
            }

            this.customersDataSource.data = updatedRows;
            this.cdr.markForCheck();
        }
    }
}
